import { useMutation, useQuery } from "@apollo/client";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { GenericCommentStream } from "components/Comments/GenericCommentStream";
import { gql } from "generated-graphql";
import { DELETE_COMMENT } from "hooks/comments";
import { useCurrentUser } from "hooks/useCurrentUser";

/**
 * The drawer's one server read. Keyed by workflow id rather than by subject, so
 * it serves every work type the queue lists (customWorkItem covers only flat
 * custom deliverables). Exported so tests can mock the real document instead of
 * a hand-rolled copy that has to stay byte-identical.
 */
export const WORKFLOW_COMMENTS_QUERY = gql(`
  query WorkflowComments($workflowId: ID!) {
    workflowComments(workflowId: $workflowId) {
      ...CommentThread
    }
  }
`);

export const UPSERT_WORK_DRAWER_COMMENT = gql(`
  mutation UpsertWorkDrawerComment($input: CommentInput!) {
    upsertComment(input: $input) {
      ...CommentThread
    }
  }
`);

/**
 * The workflow-level comment thread, sized for the inspector drawer: the same
 * thread the Custom deliverable detail page shows, so a note left in either
 * place is the same note. `useQuery` owns the list — add and delete refetch
 * rather than splicing local state, which keeps the drawer honest when two
 * staffers are commenting on the same item.
 */
export function WorkDrawerComments({ workflowId }: { workflowId: string }) {
  const alerts = useAlerts();
  const { user } = useCurrentUser();
  const { data, loading, error } = useQuery(WORKFLOW_COMMENTS_QUERY, {
    variables: { workflowId },
  });

  // The thread is drawer-local and absent from the queue grid's query, so a
  // refetch here cannot disturb grid normalization. Refetching from the
  // mutation rather than by hand also means a failed write doesn't spend a
  // round trip re-reading a thread that didn't change.
  const [upsertComment, { loading: addingComment }] = useMutation(UPSERT_WORK_DRAWER_COMMENT, {
    refetchQueries: [WORKFLOW_COMMENTS_QUERY],
    awaitRefetchQueries: true,
  });
  const [deleteComment, { loading: deletingComment }] = useMutation(DELETE_COMMENT, {
    refetchQueries: [WORKFLOW_COMMENTS_QUERY],
    awaitRefetchQueries: true,
  });

  const handleAddComment = async (message: string) => {
    try {
      await upsertComment({ variables: { input: { workflowId, comment: message } } });
    } catch (e) {
      alerts.error("Failed to add comment", e);
      // Keep the draft in the box rather than clearing it on a failed post —
      // the staffer's typed note is the one thing we can't recover.
      return { clear: false };
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await deleteComment({ variables: { id: commentId } });
    } catch (e) {
      alerts.error("Failed to delete comment", e);
    }
  };

  // Only the first load blocks: a refetch keeps the thread on screen so the
  // stream doesn't flicker every time someone posts.
  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={20} aria-label="Loading comments" />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="caption" color="error">
        Couldn&apos;t load comments for this work item.
      </Typography>
    );
  }

  return (
    <GenericCommentStream
      title=""
      comments={data?.workflowComments ?? []}
      currentUserId={user?.id}
      onAddComment={handleAddComment}
      onDeleteComment={handleDeleteComment}
      addLoading={addingComment}
      deleteLoading={deletingComment}
      placeholder="Comment on this work item…"
      buttonText="Comment"
      submitButtonVariant="icon"
      inputRows={1}
      inputMaxRows={6}
      collapseControlsWhenEmpty
      emptyStateMessage="No comments yet — this thread covers the whole work item."
    />
  );
}
